<template>
  <div class="ak-kanban__column">
    <div class="ak-kanban__column-header">
      <ak-icon
        v-if="showArrow"
        type="chevron-right"
        class="ak-kanban__column-arrow"
      />
      <h5 class="color-grey mb-none text-capitalize">
        {{ name }}
        <span v-if="total" class="text-normal color-text">({{ total }})</span>
      </h5>
    </div>
    <div class="ak-kanban__column-body">
      <ak-loading
        v-model:active="initialising"
        :can-cancel="false"
        :is-full-page="false"
      />
      <template v-if="!initialising">
        <ak-infinite-scroll :load="getMore" class="h-full">
          <ak-draggable v-model="computedData" item-key="id" group="items">
            <template #item="{ element }">
              <ak-kanban-item :component="component" :item="element" />
            </template>
          </ak-draggable>
        </ak-infinite-scroll>
      </template>
    </div>

    <div class="ak-kanban__column-footer">Add Item</div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue'
import KanbanItem from './KanbanItem.vue'
import Dragabble from '../draggable/Draggable'
import InfiniteScroll from '../infiniteScroll/InfiniteScroll.vue'
import Loading from '../loading/Loading.vue'
import Icon from '../icon/Icon.vue'

export default defineComponent({
  name: 'AkKanbanColumn',

  components: {
    [KanbanItem.name]: KanbanItem,
    [Dragabble.name]: Dragabble,
    [InfiniteScroll.name]: InfiniteScroll,
    [Loading.name]: Loading,
    [Icon.name]: Icon,
  },

  props: {
    name: {
      type: String,
      default: undefined,
    },
    data: {
      type: Object,
      default: () => ({}),
    },
    total: {
      type: Number,
      default: 0,
    },
    component: {
      type: Object,
      default: () => ({}),
    },
    fetch: {
      type: Function,
      default: () => {},
    },
    index: {
      type: Number,
      default: undefined,
    },
    columnArrow: {
      type: Boolean,
      default: true,
    },
    perPage: {
      type: Number,
      default: 20,
    },
  },

  emits: ['update:data'],

  setup(props, { emit }) {
    const initialising = ref(true)
    const isLoading = ref(false)

    const computedData = computed({
      get() {
        return props.data
      },
      set(value) {
        emit('update:data', value)
      },
    })

    const showArrow = computed(() => {
      return props.columnArrow && props.index > 0
    })

    const page = computed(() => {
      if (computedData.value && computedData.value.length) {
        return Math.ceil(computedData.value.length, props.perPage)
      } else {
        return 0
      }
    })

    const getMore = async () => {
      if (!isLoading.value) {
        isLoading.value = true
        await props.fetch({
          perPage: props.perPage,
          page: page.value,
        })
        isLoading.value = false
      }
    }

    onMounted(async () => {
      await getMore()
      initialising.value = false
    })

    return {
      getMore,
      initialising,
      computedData,
      isLoading,
      showArrow,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
.ak-kanban {
  &__column {
    min-width: 250px;
    width: 250px;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    &--won {
      background-color: rgba($color-success, 0.1);
      + .ak-kanban__column--lost {
        .ak-kanban__column-header {
          &:after {
            display: none;
          }
        }
      }
    }
    &--lost {
      background-color: rgba($color-danger, 0.1);
      + .ak-kanban__column--won {
        .ak-kanban__column-header {
          &:after {
            display: none;
          }
        }
      }
    }
    &-header {
      flex-shrink: 0;
      text-align: center;
      padding: $spacing-small;
      color: $color-grey;
      position: relative;
    }
    &-arrow {
      position: absolute;
      top: 50%;
      left: -1rem;
      transform: translateY(-50%);
      border-radius: 50%;
      height: 2rem;
      width: 2rem;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: $color-grey-lightest;
      border: 1px solid $color-grey-lighter;
      color: $color-grey-light;
    }
    &-body {
      flex: 1 0 auto;
      height: 0;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      -ms-overflow-style: -ms-autohiding-scrollbar;
      padding: $spacing-small;
      position: relative;
    }
    &-footer {
      flex-shrink: 0;
      text-align: center;
      padding: $spacing-tiny $spacing-small $spacing-small;
      border-top: $base-border;
      color: $color-grey-light;
      transition: $transition;
      &:hover {
        color: $color-action;
      }
    }
    &-empty {
      margin: 0;
      padding: $spacing $spacing-small $spacing-small;
      font-size: 85%;
      font-style: italic;
      font-weight: bold;
      line-height: 1.4;
      text-align: center;
    }
    &-loading {
      font-size: 85%;
      padding: $spacing $spacing-small;
      > * {
        margin-left: auto;
        margin-right: auto;
        line-height: 1;
      }
      &--middle {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translateY(-50%) translateX(-50%);
      }
    }
  }
}
</style>
